import { AnalysisConfig } from "../models/AnalysisConfig";

export class AnalysisConfigSearchResult {
  constructor(
    public analysisConfigId: string,
    public analysisConfig: AnalysisConfig,
    public path: Array<number>,
    public topLevelParent: AnalysisConfig | undefined,
    public parent?: AnalysisConfig
  ) {}
}

export class AnalysisConfigSearchService {
  public static findAnalysisConfigByIdRecursive(
    analysisConfigs: AnalysisConfig[],
    targetId: string
  ): AnalysisConfigSearchResult | null {
    return this.search(analysisConfigs, targetId);
  }

  private static search(
    analysisConfigs: AnalysisConfig[],
    targetId: string,
    parent?: AnalysisConfig,
    topLevelParent?: AnalysisConfig,
    path: number[] = []
  ): AnalysisConfigSearchResult | null {
    for (let i = 0; i < analysisConfigs.length; i++) {
      const analysisConfig = analysisConfigs[i];
      const currentPath = [...path, i];
      const currentTopLevel = topLevelParent ?? analysisConfig;

      if (analysisConfig.id === targetId) {
        return new AnalysisConfigSearchResult(targetId, analysisConfig, currentPath, currentTopLevel, parent);
      }

      const nestedConfigs = analysisConfig.configuration?.analysis_configs;
      if (Array.isArray(nestedConfigs)) {
        // Nested analysis configs are not supported in the original plotting implementation,
        // keep same behavior to avoid surprising structural changes.
        throw new Error("AnalysisConfigs containing child analysis configs is not supported.");
        // If support is added later, the search recursion would be:
        // const result = this.search(nestedConfigs, targetId, analysisConfig, currentTopLevel, currentPath);
        // if (result) return result;
      }
    }

    return null;
  }

  public static setAnalysisConfigAtPath(
    topLevelParent: AnalysisConfig,
    path: number[],
    newAnalysisConfig: AnalysisConfig
  ): boolean {
    if (path.length === 0) return false; // Nothing to replace

    let currentConfigs = topLevelParent.configuration?.analysis_configs;
    if (!Array.isArray(currentConfigs)) return false;

    // Traverse to the parent of the target config
    for (let i = 0; i < path.length - 1; i++) {
      const index = path[i];
      const cfg = currentConfigs[index];
      if (!cfg?.configuration?.analysis_configs) return false;

      currentConfigs = cfg.configuration.analysis_configs;
    }

    const targetIndex = path[path.length - 1];

    // Replace the target config
    if (currentConfigs && currentConfigs[targetIndex]) {
      currentConfigs.splice(targetIndex, 1, newAnalysisConfig);
      return true;
    }

    return false;
  }
}
